import { useState } from 'react';
import { Modal, Form, Input, App, Typography, Button, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../../hooks/useAuth';
import { getRecaptchaConfig } from '../../services/clientConfigService';
import systemConfigService from '../../api/systemConfigService';

const { Text } = Typography;
const { TextArea } = Input;

const RECAPTCHA_ACTION = 'signin';

interface ConfigRevealModalProps {
  configKey: string | null;
  open: boolean;
  onClose: () => void;
  onRevealed?: (value: string) => void;
  /** When true (default), show the revealed value in this modal */
  showRevealedValue?: boolean;
}

export const ConfigRevealModal = ({
  configKey,
  open,
  onClose,
  onRevealed,
  showRevealedValue = true,
}: ConfigRevealModalProps) => {
  const { message } = App.useApp();
  const { authState } = useAuth();
  const token = authState.token;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const recaptchaConfig = getRecaptchaConfig();
  const [form] = Form.useForm<{ password: string }>();
  const [submitting, setSubmitting] = useState(false);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);

  const handleClose = () => {
    form.resetFields();
    setRevealedValue(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!configKey || !token) {
      message.error('Authentication required');
      return;
    }

    try {
      const values = await form.validateFields();
      const isRecaptchaActive = recaptchaConfig.enabled && recaptchaConfig.siteKey;
      if (isRecaptchaActive && !executeRecaptcha) {
        message.error('reCAPTCHA not loaded. Please refresh the page.');
        return;
      }

      setSubmitting(true);
      const recaptchaToken = isRecaptchaActive && executeRecaptcha
        ? await executeRecaptcha(RECAPTCHA_ACTION)
        : '';

      const config = await systemConfigService.revealConfig(
        configKey,
        values.password,
        recaptchaToken,
        token
      );

      const value = config.config_value ?? '';
      onRevealed?.(value);

      if (showRevealedValue) {
        setRevealedValue(value);
        form.resetFields();
      } else {
        handleClose();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to reveal value';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (revealedValue === null) return;
    try {
      await navigator.clipboard.writeText(revealedValue);
      message.success('Copied to clipboard');
    } catch {
      message.error('Failed to copy');
    }
  };

  return (
    <Modal
      title={revealedValue !== null ? 'Configuration value' : 'Confirm password to show value'}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      footer={
        revealedValue !== null ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <Space>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              Show value
            </Button>
          </Space>
        )
      }
    >
      {revealedValue !== null ? (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            <Text code>{configKey}</Text>
          </Text>
          <TextArea
            value={revealedValue}
            readOnly
            rows={4}
            style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 8 }}
          />
          <Button icon={<CopyOutlined />} onClick={handleCopy}>
            Copy
          </Button>
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Re-enter your Tomorrow.io password to view <Text code>{configKey}</Text>.
          </Text>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
